/**
 * SampleRegistrationDetailPage — the Sample Registration detail view
 * (Class C transaction BO), built from the approved design prototype
 * `29131-AI_Sample Registration/Sample-Tracking-Module-Prototype.html`
 * (2026-07-18) and the legacy XAF view
 * `Samplecheckin_DetailView_Copy_SampleRegistration`.
 *
 * ── ANATOMY (BO UI Standard §5 · Class C) ───────────────────────────────────
 *   · breadcrumb → <FormTitle> (nav icon/colour + load chip + audit chip)
 *   · the shared <StepIsland>, portalled into the AppShell top bar:
 *       Registration · Samples · Tests · Containers · Save · Submit
 *     Step state is computed LIVE from the data (never a static wizard).
 *   · NO step-hint banner — the prototype hides hints app-wide (2026-07-18:
 *     "not very useful once the user knows what to do, the stepper is enough")
 *   · the XAF action bar: form-specific actions LEFT · Save · Save and Close ·
 *     Save and New · | · Cancel · Back/Next — top, and repeated at the bottom
 *     on the long Registration step only
 *   · the step body: Registration (3 field sections + 7 tabs) or the Sample
 *     Login workspace (Sample Login / Test View / Containers / Parameters) or
 *     the Save / Submit review steps
 *   · the flow bar: Samples · Tests · Containers CTAs with live counts
 *
 * ── WHAT IS AND IS NOT WIRED (AI-29131, data-source handoff) ─────────────────
 * The list view, detail view and lookup catalogs now read real data:
 *   · GET /api/v1/sample-registration
 *   · GET /api/v1/sample-registration/{jobId}
 *   · GET /api/v1/sample-registration/lookups?matrixName=
 *   · GET /api/v1/lookups/employees  — the GLOBAL employee lookup that feeds
 *     "Received By" (shared by every form, not owned by this one)
 * Save / submit / delete and child-collection persistence are still TODO:
 * every persistence point remains funnelled through `buildPayload()` + `persist()`
 * so wiring the write endpoints stays surgical.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { fetchEmployeeLookup } from '../../../api/lookups';
import { FormTitle } from '../../../components/FormTitle';
import { TopbarStepper } from '../../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../../components/StepIsland/StepIsland';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { LoaderSymbol } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useSaveFlow, warn as warnDialog } from '../../../components/confirm';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { RegistrationForm } from './RegistrationForm';
import { RegistrationTabs } from './RegistrationTabs';
import { SampleLoginView, type SlTab, cachedParamOid } from './SampleLoginView';
import { NewLookupDialog } from './NewLookupDialog';
import {
  LOOKUPS, SAMPLE_STATUS_OPTS, autoBottles, chargeRows, cloneTest, emptyHeader,
  copyTest, emptyTabState, mkTest, nextKey, renumber, resolveDueDate, sampleId, toDateInput, toDateTimeInput,
  eddFormatToInt, eddFormatFromInt,
  SCC_POINTS,
  type SrAttachment, type SrHeader, type SrImage, type SrItemCharge,
  type SrLookup, type SrLookupKey, type SrNote, type SrReceivedDate,
  type SrSample, type SrSccComment, type SrSccRecord, type SrTabState, type SrTest,
} from './srData';
import './sampleRegistration.css';

/** The six workflow steps (prototype STEPS). */
const STEPS = ['Registration', 'Samples', 'Tests', 'Containers', 'Save', 'Submit'] as const;
type StepIndex = 0 | 1 | 2 | 3 | 4 | 5;

/** The audit FormName for this form (§14b.2 registry). */
export const SAMPLE_REGISTRATION_FORM = 'Sample Registration';

interface ApiSuccess<T> {
  success: true;
  data: T;
}

interface SampleRegistrationDetailDto {
  jobId: string;
  oid: string;
  receivedDate: string;
  receivedBy: string;
  createdBy: string;
  client: string;
  clientOid: string;
  clientAddress: string;
  clientPhone: string;
  clientContact: string;
  quoteId: string;
  poNumber: string;
  paymentStatus: string;
  manifestNumber: number | null;
  projectManager: string;
  dateExpect: string;
  relinquishedBy: string;
  collector: string;
  dateCollected: string;
  timeCollected: string;
  projectId: string;
  projectName: string;
  projectLocation: string;
  permit: string;
  projectCategory: string;
  cocId: string;
  invoiceContact: string;
  registrationType: string;
  jobInactivation: string;
  statusName: string;
  matrices: string;
  testTemplate: string;
  sampleCategory: string;
  coolerId: string;
  noOfSamples: number;
  remark: string;
  tat: string;
  dueDate: string;
  reportTemplate: string;
  eddTemplate: string;
  eddFormat: string;
  reportDeliveryAddress: string;
  reportDeliveryMethod: string;
}

interface SampleRegistrationLookupRowDto {
  lookupKey: string;
  col1: string;
  col2: string;
  col3: string;
  col4: string;
  // Oid string — the FK the API needs at Save time. Empty for text/enum lookups.
  col5?: string;
}

/** SampleLogIn row from GET .../{oid}/sample-logins (RS1). */
interface SampleLoginRowDto {
  jobIdStr:        string;
  oid:             string;
  sampleNo:        number;
  clientSampleId:  string | null;
  visualMatrix:    string | null;
  sampleType:      string | null;
  qty:             number | null;
  collector:       string | null;
  collectDate:     string | null;
  collectTime:     string | null;
  description:     string | null;
  comment:         string | null;
  qcCategory:      string | null;
  systemType:      string | null;
  siteNameOid:     string | null;
  hold:            boolean;
  subOut:          boolean;
  samplingStatus:  number;
  loginBy:         string | null;
  loginDateTime:   string | null;
  lastUpdatedBy:   string | null;
  lastUpdatedDate: string | null;
  // Additional configurable columns
  address:              string | null;
  keyMap:               string | null;
  samplingAddress:      string | null;
  samplePointID:        string | null;
  samplePointType:      string | null;
  pwsidOid:             string | null;
  waterSource:          string | null;
  rejectionCriteriaOid: string | null;
  repeatLocationOid:    string | null;
  departmentOid:        string | null;
  sampleStatusOid:      string | null;
  parentSampleID:       string | null;
  parentSampleDate:     string | null;
  sysSampleCode:        string | null;
  projectNumber:        string | null;
  plannedLatitude:      string | null;
  plannedLongitude:     string | null;
  chlorineFree:         string | null;
  chlorineTotal:        string | null;
  flowRate:             string | null;
  volume:               string | null;
  time:                 string | null;
  startFlow:            string | null;
  endFlow:              string | null;
  timeStart:            string | null;
  timeEnd:              string | null;
  remark:               string | null;
  collectorPhone:       string | null;
  samplingEvent:        string | null;
  alternativeStation:   string | null;
  excludeInvoice:       boolean;
  notReport:            boolean;
  complianceProject:    boolean;
  dueDate:              string | null;
  restTime:             string | null;
  ph:                   string | null;
  do:                   string | null;
  standardVol:          string | null;
  absorptionvol:        string | null;
  trafficFlowBigVechile:    string | null;
  trafficFlowMiddleVechile: string | null;
  trafficFlowSmallVechile:  string | null;
  receivedDateOid:      string | null;
}

/** SampleParameter row from GET .../{oid}/sample-logins (RS2). */
interface SampleParamDto {
  sampleloginOid: string;
  oid:            string;
  testMethodOid:  string | null;
  testName:       string | null;
  methodNumber:   string | null;
  component:      string | null;
  testGroupName:  string | null;
  testHold:       boolean;
  tatOid:         string | null;
  tatName:        string | null;
  dateReceived:   string | null;
  subOut:         boolean;
  status:         number;
}

/** Combined payload from GET .../{oid}/sample-logins. */
interface SampleLoginsDto {
  samples:    SampleLoginRowDto[];
  parameters: SampleParamDto[];
}

/** One Custom Request override row from GET .../{oid}/custom-requests. */
interface CustomRequestRowDto {
  testName: string;
  tatName:  string | null;
  tatOid:   string | null;
  dueDate:  string | null;
  hold:     boolean;
}

/** One Received Dates row from GET .../{oid}/received-dates (FK cols resolved). */
interface ReceivedDateRowDto {
  oid: string;
  receivedDate: string | null;
  receivedBy: string;
  relinquishedBy: string;
  tat: string;
  sampleMatrix: string;
  sampleCategory: string;
  coolerId: string;
  noOfSamples: number;
  createdBy: string;
  createdDate: string | null;
}

/** Map an API received-date row → the sub-tab's SrReceivedDate shape. */
function mapReceivedDateRow(r: ReceivedDateRowDto): SrReceivedDate {
  return {
    key: `rd-srv-${r.oid}`,
    oid: r.oid,
    date: r.receivedDate ?? '',
    by: r.receivedBy ?? '',
    relinq: r.relinquishedBy ?? '',
    n: r.noOfSamples ?? 1,
    matrix: (r.sampleMatrix ?? '').split('; ').filter(Boolean),
    tat: r.tat ?? '',
    cooler: r.coolerId ?? '',
    cat: (r.sampleCategory ?? '').split('; ').filter(Boolean),
    createdBy: r.createdBy ?? '',
    created: r.createdDate ?? '',
  };
}

// ── Child-tab API DTOs + mapping helpers ───────────────────────────────────

interface NoteRowDto {
  oid: string;
  title: string;
  author: string;
  authorOid: string;
  date: string | null;
  sort: number;
  html: string;
  plainText: string;
  sourceId: string;
  noteSource: string;
}

function mapNoteRow(r: NoteRowDto): SrNote {
  return {
    key: `note-srv-${r.oid}`, oid: r.oid, title: r.title ?? '',
    author: r.author ?? '', authorOid: r.authorOid ?? '',
    date: r.date ?? '', sort: r.sort ?? 0,
    html: r.html ?? '', plainText: r.plainText ?? '',
    sourceId: r.sourceId ?? '', noteSource: r.noteSource ?? '',
  };
}

interface SccDto {
  oid: string;
  checkInBy: string;
  checkInDate: string | null;
  visualMatrix: string;
  temperature: string;
  correctedTemperature: string;
  thermometerID: string;
  samplePH: string;
  phpaperID: string;
  receivedDate: string;
  checkPoints: { checkPointName: string; isYes: boolean; isNo: boolean; isNA: boolean; description: string }[];
  comments: { comment: string; userName: string; commentDateTime: string | null }[];
}

function mapSccRow(r: SccDto): SrSccRecord {
  const pointMap: Record<string, string> = {};
  const descMap: Record<string, string> = {};
  for (const cp of r.checkPoints ?? []) {
    pointMap[cp.checkPointName] = cp.isYes ? 'yes' : cp.isNo ? 'no' : 'na';
    descMap[cp.checkPointName] = cp.description ?? '';
  }
  return {
    key: `scc-srv-${r.oid}`, oid: r.oid,
    matrix: r.visualMatrix ?? '', by: r.checkInBy ?? '',
    date: r.checkInDate ?? '', recv: r.receivedDate ?? '',
    temp: r.temperature ?? '', corr: r.correctedTemperature ?? '',
    thermo: r.thermometerID ?? '', ph: r.samplePH ?? '', paper: r.phpaperID ?? '',
    points: SCC_POINTS.map(p => pointMap[p[0]] ?? p[1]),
    descriptions: SCC_POINTS.map(p => descMap[p[0]] ?? ''),
    comments: (r.comments ?? []).map((c): SrSccComment => ({
      text: c.comment ?? '', user: c.userName ?? '', dt: c.commentDateTime ?? '',
    })),
  };
}

interface ItemChargeRowDto {
  oid: string;
  itemName: string;
  description: string;
  itemPriceOid: string;
  unitPrice: number | null;
  qty: number | null;
  amount: number | null;
  finalAmount: number | null;
  npUnitPrice: number | null;
  discount: number | null;
  remark: string;
}

function mapItemChargeRow(r: ItemChargeRowDto): SrItemCharge {
  return {
    key: `ic-srv-${r.oid}`, oid: r.oid,
    itemPriceOid: r.itemPriceOid ?? '',
    name: r.itemName ?? '',
    desc: r.description ?? '',
    price: r.unitPrice ?? 0, qty: r.qty ?? 0,
    amount: r.amount ?? 0, final: r.finalAmount ?? 0,
    npPrice: r.npUnitPrice ?? 0, disc: r.discount ?? 0,
    remark: r.remark ?? '',
  };
}

interface AttachmentRowDto {
  oid: string;
  name: string;
  category: number;
  operator: string;
  operatorOid: string;
  date: string | null;
  comment: string;
  fileDataOid: string;
  receivedDate: string;
  fileName: string;
}

function mapAttachmentRow(r: AttachmentRowDto): SrAttachment {
  return {
    key: `att-srv-${r.oid}`, oid: r.oid,
    name: r.name ?? '', cat: String(r.category ?? 5),
    file: r.fileName ?? '', fileDataOid: r.fileDataOid ?? '',
    date: r.date ?? '', op: r.operator ?? '', opOid: r.operatorOid ?? '',
    recv: r.receivedDate ?? '', comment: r.comment ?? '',
  };
}

interface ImageRowDto {
  oid: string;
  name: string;
  comment: string;
  sort: number;
  fileSize: number | null;
}

function mapImageRow(r: ImageRowDto): SrImage {
  return {
    key: `img-srv-${r.oid}`, oid: r.oid,
    name: r.name ?? '', photo: '', sort: r.sort ?? 0,
    comment: r.comment ?? '',
  };
}

export interface SampleRegistrationDetailPageProps {
  /** null = New; a Job ID = Edit. */
  jobId: string | null;
  /** Registration Type chosen in the New dialog ('Regular' | 'Pre-scheduled'). */
  registrationType: string;
  /** Optional list-row values shown while the detail GET is loading. */
  seed?: { client: string; projectName: string; status: string; receivedDate: string | null; dueDate: string | null };
  /** Ordered Job IDs of the current list page — powers the prev/next arrows. */
  siblingIds?: string[];
  /** Current user (Created By / Received By / author). */
  user: string;
  onHome: () => void;
  /** Cancel / Save and Close → back to the list. */
  onClose: () => void;
  /** Jump to another record (prev/next arrows). */
  onNavigate: (jobId: string) => void;
  /** Refresh the list behind the detail after a save. */
  onSaved: () => void;
  /** Open the Sample Container pH Check form. */
  onOpenPhCheck?: () => void;
}

export default function SampleRegistrationDetailPage({
  jobId, registrationType, seed, siblingIds = [], user,
  onHome, onClose, onNavigate, onSaved, onOpenPhCheck,
}: SampleRegistrationDetailPageProps) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();

  // ── State ─────────────────────────────────────────────────────────────────
  // Server-local "now" — the form stamps drafts with it (§14b.6: while the UTC
  // kill-switch is on, dates are saved and shown as the server's wall clock).
  const nowLocal = useMemo(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  const [currentJobId, setCurrentJobId] = useState<string | null>(jobId);
  const [jobOid, setJobOid] = useState<string | null>(null);
  // Received Dates are only authoritative for the save once loaded. A NEW job
  // has nothing to load (sub-tab starts empty), so it's "loaded" immediately;
  // an EXISTING job stays false until its SC_ReceivedDates are fetched, so a
  // premature save can't wipe them (persist sends null until loaded).
  const [rdLoaded, setRdLoaded] = useState<boolean>(!jobId);
  const [notesLoaded, setNotesLoaded] = useState<boolean>(!jobId);
  const [sccLoaded, setSccLoaded] = useState<boolean>(!jobId);
  const [icLoaded, setIcLoaded] = useState<boolean>(!jobId);
  const [attLoaded, setAttLoaded] = useState<boolean>(!jobId);
  const [imgLoaded, setImgLoaded] = useState<boolean>(!jobId);
  const [step, setStep] = useState<StepIndex>(0);
  const [slTab, setSlTab] = useState<SlTab>('login');
  const [header, setHeader] = useState<SrHeader>(() => seedHeader(jobId, registrationType, nowLocal, user, seed));
  const [samples, setSamples] = useState<SrSample[]>([]);
  const [active, setActive] = useState(0);
  const [tabs, setTabs] = useState<SrTabState>(() => seedTabs(nowLocal, user));
  const [customRequest, setCustomRequest] = useState<Record<string, { hold: boolean; due: string; tat: string }>>({});
  const [lookups, setLookups] = useState<Record<SrLookupKey, SrLookup>>(() => cloneLookups());
  const [groupChildren, setGroupChildren] = useState<Record<string, { name: string; method: string; comp: string }[]>>({});
  const [newLookupKind, setNewLookupKind] = useState<SrLookupKey | null>(null);
  const [newLookupField, setNewLookupField] = useState<keyof SrHeader | null>(null);
  const [importKind, setImportKind] = useState<'quote' | 'coc' | null>(null);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [triedSave, setTriedSave] = useState(false);
  const [flash, setFlash] = useState<Set<string>>(new Set());
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [tatHoursMap, setTatHoursMap] = useState<Record<string, number>>({});
  const [holidaySet, setHolidaySet] = useState<ReadonlySet<string>>(new Set());
  const [statusNameMap, setStatusNameMap] = useState<Record<string, string>>({});

  const isNew = currentJobId === null;
  const patch = useCallback((p: Partial<SrHeader>) => {
    dirtyRef.current = true;
    setHeader(h => ({ ...h, ...p }));
  }, []);

  // ── Dirty-form guard ────────────────────────────────────────────────────
  // Tracks whether the user has modified the form since load.  Any `patch()`
  // call sets it; loading from API or resetting clears it.
  const dirtyRef = useRef(false);
  // ── Received-date auto-seed guard ───────────────────────────────────────
  // Fires once per new-job session (XAF parity: one receipt event created
  // automatically from the header when the user first picks a matrix).
  const rdAutoSeededRef = useRef(false);

  const guardedExit = useCallback(async (action: () => void) => {
    if (!dirtyRef.current) { action(); return; }
    const ok = await confirm({
      title: 'Unsaved Changes',
      message: 'Unsaved changes will be lost. Do you want to proceed?',
      confirmLabel: 'OK',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (ok) { dirtyRef.current = false; action(); }
  }, [confirm]);

  // ── Browser-level unsaved-changes guard (refresh / close tab / navigate away)
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    dirtyRef.current = false;
    setCurrentJobId(jobId);
    setStep(0);
    setSlTab('login');
    setSamples([]);
    setActive(0);
    setTabs(seedTabs(nowLocal, user));
    setCustomRequest({});
    setTriedSave(false);
    setFlash(new Set());
    setDetailError(null);
    setHeader(seedHeader(jobId, registrationType, nowLocal, user, seed));
    // Reset loaded flags — new job = nothing to load (true), existing = wait for GET (false)
    const isNewJob = !jobId;
    setRdLoaded(isNewJob);
    setNotesLoaded(isNewJob);
    setSccLoaded(isNewJob);
    setIcLoaded(isNewJob);
    setAttLoaded(isNewJob);
    setImgLoaded(isNewJob);
    rdAutoSeededRef.current = false;
  }, [jobId, registrationType, nowLocal, user, seed]);

  // ── Received Dates auto-seed (XAF parity) ───────────────────────────────
  // In XAF, a first receipt-event row is auto-created from the form header
  // the moment the user picks at least one matrix on a new job.  Mirror that:
  // fire once per new-job session, as soon as matrices.length goes from 0→1.
  // The ref guard ensures it never fires a second time even if matrices change.
  useEffect(() => {
    if (!isNew) return;
    if (rdAutoSeededRef.current) return;
    if (header.matrices.length === 0) return;
    if (tabs.receivedDates.length > 0) return;
    rdAutoSeededRef.current = true;
    const row: SrReceivedDate = {
      key:       nextKey('rd'),
      oid:       '',
      date:      header.receivedDate,
      by:        header.receivedBy,
      relinq:    '',
      n:         Math.max(1, parseInt(header.noOfSamples, 10) || 1),
      matrix:    [...header.matrices],
      tat:       header.tat,
      cooler:    '',
      cat:       [...header.sampleCategory],
      createdBy: header.receivedBy || user,
      created:   nowLocal,
    };
    setTabs(t => ({ ...t, receivedDates: [row] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, header.matrices.length]);

  // ── "Received By" data source — the GLOBAL employee lookup ────────────────
  // Loaded from GET /api/v1/lookups/employees (dbo.AI_Employee_Lookup_SP), NOT
  // from this form's own lookups SP: one roster, shared by every form that needs
  // an employee picker (CLAUDE.md §14b "don't reinvent").  Fetched once per
  // mount — it must NOT re-fetch when the matrix changes, unlike the SR lookups.
  //
  // ONE column: Employee.DisplayName straight from the table, in the SP's own
  // ASC order (no client-side re-sort).  Rows whose DisplayName is blank are
  // dropped — they would render as an unpickable empty line.
  const [employeeRows, setEmployeeRows] = useState<string[][]>([]);
  const [employeeOidByLabel, setEmployeeOidByLabel] = useState<Record<string, string>>({});
  useEffect(() => {
    const ac = new AbortController();
    void fetchEmployeeLookup(undefined, ac.signal)
      .then(list => {
        const rows: string[][] = [];
        const oidMap: Record<string, string> = {};
        for (const e of list) {
          const name = e.displayName.trim();
          if (!name) continue;
          rows.push([name]);
          if (e.oid) oidMap[name] = e.oid;
        }
        setEmployeeRows(rows);
        setEmployeeOidByLabel(oidMap);
      })
      .catch(() => {
        // Non-fatal: the picker stays empty and the field remains free-text.
        // The server already logged the failure (ExceptionLog middleware).
      });
    return () => ac.abort();
  }, []);

  // ── Rule data (TAT hours + holidays) for due-date calculation ──────────────
  useEffect(() => {
    const ac = new AbortController();
    void api.get('/api/v1/sample-registration/rule-data', { signal: ac.signal })
      .then(res => {
        const d = (res.data as ApiSuccess<{ tatEntries: { tatName: string; hours: number; isDefault: boolean }[]; holidays: string[]; statuses: { statusId: string; statusName: string }[] }>).data;
        const map: Record<string, number> = {};
        for (const e of d.tatEntries) map[e.tatName] = e.hours;
        setTatHoursMap(map);
        setHolidaySet(new Set(d.holidays));
        // Status ID → display name map for the inactivation state machine
        const smap: Record<string, string> = {};
        for (const s of (d.statuses ?? [])) smap[s.statusId] = s.statusName;
        setStatusNameMap(smap);
        // XAF parity: pre-populate the TAT field with the default entry on new jobs
        const def = d.tatEntries.find(e => e.isDefault);
        if (def && !header.tat) setHeader(h => ({ ...h, tat: def.tatName }));
      })
      .catch(() => { /* non-fatal: due-date auto-calc won't fire */ });
    return () => ac.abort();
  }, []);

  /** Lookups handed to the form: the SR catalogs plus the global employee roster.
   *  Every lookup carries an oidByLabel map (populated from Col5 of the SP for
   *  the SR catalogs, from EmployeeLookupItem.oid for the global roster) so
   *  persist() can resolve display → FK Oid at Save time. */
  const formLookups = useMemo<Record<SrLookupKey, SrLookup>>(
    () => ({
      ...lookups,
      employee: {
        cols: lookups.employee.cols,
        rows: employeeRows,
        oidByLabel: employeeOidByLabel,
      },
    }),
    [lookups, employeeRows, employeeOidByLabel]);

  const loadLookups = useCallback(async (matrixName?: string, clientName?: string, clientOid?: string) => {
    const params: Record<string, string> = {};
    if (matrixName) params.matrixName = matrixName;
    if (clientName) params.clientName = clientName;
    // Prefer the exact client Oid — unambiguous when client names collide.
    if (clientOid) params.clientOid = clientOid;
    const res = await api.get('/api/v1/sample-registration/lookups', {
      params: Object.keys(params).length ? params : undefined,
    });
    const payload = (res.data as ApiSuccess<SampleRegistrationLookupRowDto[]>).data ?? [];
    setLookups(mapLookupRows(payload));
    const gc: Record<string, { name: string; method: string; comp: string }[]> = {};
    for (const row of payload) {
      if (row.lookupKey !== 'testgroup') continue;
      const grpName = row.col1 ?? '';
      if (!grpName) continue;
      (gc[grpName] ??= []).push({ name: row.col2 ?? '', method: row.col3 ?? '', comp: row.col4 ?? '' });
    }
    setGroupChildren(gc);
  }, []);

  useEffect(() => {
    let dead = false;
    const readDetail = async () => {
      if (!jobId) {
        setDetailError(null);
        return;
      }
      setDetailLoading(true);
      setDetailError(null);
      try {
        const res = await api.get(`/api/v1/sample-registration/${encodeURIComponent(jobId)}`);
        const dto = (res.data as ApiSuccess<SampleRegistrationDetailDto>).data;
        if (dead) return;
        if (dto.oid) setJobOid(dto.oid);
        setHeader(mapDetailToHeader(dto, registrationType, nowLocal, user));
        dirtyRef.current = false;

        // Load all child tab data so they can be edited/saved.
        if (dto.oid) {
          const oidEnc = encodeURIComponent(dto.oid);
          const loadChild = async <T,>(
            path: string,
            map: (r: T) => unknown,
            tabKey: keyof SrTabState,
            setLoaded: (v: boolean) => void,
            isSingle?: boolean,
          ) => {
            try {
              const res = await api.get(`/api/v1/sample-registration/${oidEnc}/${path}`);
              if (dead) return;
              const raw = (res.data as ApiSuccess<T | T[]>).data;
              if (isSingle) {
                const mapped = raw ? [map(raw as T)] : [];
                setTabs(t => ({ ...t, [tabKey]: mapped }));
              } else {
                const arr = (raw as T[] | null) ?? [];
                setTabs(t => ({ ...t, [tabKey]: arr.map(map) }));
              }
            } catch {
              /* non-fatal: the sub-tab stays empty; server logged the failure */
            } finally {
              if (!dead) setLoaded(true);
            }
          };

          // Load Custom Request overrides so the tab restores on reopen.
          const loadCustomRequests = async () => {
            try {
              const res = await api.get(`/api/v1/sample-registration/${oidEnc}/custom-requests`);
              if (dead) return;
              const rows = ((res.data as ApiSuccess<CustomRequestRowDto[]>).data) ?? [];
              if (rows.length > 0) {
                const map: Record<string, { hold: boolean; due: string; tat: string }> = {};
                for (const r of rows) {
                  map[r.testName] = {
                    hold: r.hold,
                    due:  r.dueDate ?? '',
                    tat:  r.tatName ?? '',
                  };
                }
                setCustomRequest(map);
              }
            } catch {
              /* non-fatal */
            }
          };

          // Load persisted SampleLogIn + SampleParameter rows so Edit Mode restores
          // the top and bottom grids from the DB instead of re-generating from noOfSamples.
          const loadSampleLogins = async () => {
            try {
              const res = await api.get(`/api/v1/sample-registration/${oidEnc}/sample-logins`);
              if (dead) return;
              const payload = (res.data as ApiSuccess<SampleLoginsDto | null>).data;
              if (!payload || !payload.samples?.length) return;

              // Group SampleParameter rows by their SampleLogIn Oid
              const paramsByOid = new Map<string, SampleParamDto[]>();
              for (const p of payload.parameters ?? []) {
                const arr = paramsByOid.get(p.sampleloginOid) ?? [];
                arr.push(p);
                paramsByOid.set(p.sampleloginOid, arr);
              }

              const mapped: SrSample[] = payload.samples.map(row => {
                const id = `${row.jobIdStr}-${String(row.sampleNo).padStart(3, '0')}`;
                const v: Record<string, string> = {
                  SampleNo:        String(row.sampleNo),
                  SampleName:      row.clientSampleId  ?? '',
                  SampleMatrix:    row.visualMatrix     ?? '',
                  SampleType:      row.sampleType       ?? '',
                  Qty:             row.qty != null ? String(row.qty) : '1',
                  Collector:       row.collector        ?? '',
                  DateCollected:   row.collectDate ? row.collectDate.slice(0, 10) : '',
                  TimeCollected:   row.collectTime      ?? '',
                  Description:     row.description      ?? '',
                  Remark:          row.comment          ?? '',
                  QCCategory:      row.qcCategory       ?? '',
                  SystemType:      row.systemType       ?? '',
                  SiteName:        row.siteNameOid      ?? '',
                  Hold:            row.hold ? '1' : '0',
                  SubOut:          row.subOut ? '1' : '0',
                  SamplingStatus:  String(row.samplingStatus ?? 0),
                  EnteredBy:       row.loginBy           ?? '',
                  EnteredDate:     row.loginDateTime     ?? '',
                  LastUpdatedBy:   row.lastUpdatedBy     ?? '',
                  LastUpdatedDate: row.lastUpdatedDate   ?? '',
                  // Additional configurable columns
                  Address:            row.address            ?? '',
                  KeyMap:             row.keyMap             ?? '',
                  SamplingAddress:    row.samplingAddress    ?? '',
                  SamplePointID:      row.samplePointID     ?? '',
                  SamplePointType:    row.samplePointType   ?? '',
                  PWSID:              row.pwsidOid          ?? '',
                  WaterType:          row.waterSource        ?? '',
                  RejectionCriteria:  row.rejectionCriteriaOid ?? '',
                  RepeatLocation:     row.repeatLocationOid ?? '',
                  Department:         row.departmentOid     ?? '',
                  SampleStatus:       row.sampleStatusOid   ?? '',
                  ParentSampleID:     row.parentSampleID    ?? '',
                  ParentSampleDate:   row.parentSampleDate  ?? '',
                  SysSampleCode:      row.sysSampleCode     ?? '',
                  ProjectNumber:      row.projectNumber     ?? '',
                  PlannedLatitude:    row.plannedLatitude    ?? '',
                  PlannedLongitude:   row.plannedLongitude   ?? '',
                  'FreeColorine(mg/L)': row.chlorineFree    ?? '',
                  'TotalChlorine(mg/L)': row.chlorineTotal  ?? '',
                  FlowRate:           row.flowRate           ?? '',
                  Volume:             row.volume             ?? '',
                  Time:               row.time               ?? '',
                  StartFlow:          row.startFlow          ?? '',
                  EndFlow:            row.endFlow            ?? '',
                  TimeStart:          row.timeStart          ?? '',
                  TimeEnd:            row.timeEnd            ?? '',
                  Comment:            row.comment            ?? '',
                  CollectorPhone:     row.collectorPhone     ?? '',
                  SamplingEvent:      row.samplingEvent      ?? '',
                  AlternativeStation: row.alternativeStation ?? '',
                  ExcludeInvoice:     row.excludeInvoice ? '1' : '0',
                  NotReport:          row.notReport ? '1' : '0',
                  ComplianceProject:  row.complianceProject ? '1' : '0',
                  DueDate:            row.dueDate ? row.dueDate.slice(0, 10) : '',
                  RestTime:           row.restTime           ?? '',
                  PH:                 row.ph                 ?? '',
                  DO:                 (row as unknown as Record<string, string>)['do'] ?? '',
                  StandardVol:        row.standardVol        ?? '',
                  Absorptionvol:      row.absorptionvol      ?? '',
                  TrafficFlowBigVechile:    row.trafficFlowBigVechile    ?? '',
                  TrafficFlowMiddleVechile: row.trafficFlowMiddleVechile ?? '',
                  TrafficFlowSmallVechile:  row.trafficFlowSmallVechile  ?? '',
                  ReceivedDate:       row.receivedDateOid    ?? '',
                };

                const params = paramsByOid.get(row.oid) ?? [];
                // Group SampleParameter rows by TestMethod+Component so the
                // grid shows ONE row per test+component, not one per analyte.
                const grouped = new Map<string, SampleParamDto>();
                for (const p of params) {
                  if (!p.testMethodOid || !p.testName) continue;
                  const gk = `${p.testMethodOid}|${p.component ?? ''}`;
                  if (!grouped.has(gk)) grouped.set(gk, p);
                }
                const tests: SrTest[] = [...grouped.values()].map(p => {
                    const t = mkTest(
                      p.testName!,
                      p.methodNumber ?? '',
                      p.component    ?? '',
                      '',
                      p.testGroupName ?? '',
                    );
                    t.key    = `${p.testMethodOid}|${p.component ?? ''}`;
                    t.hold   = p.testHold;
                    t.subOut = p.subOut;
                    t.status = SAMPLE_STATUS_OPTS[p.status ?? 0] ?? '';
                    return t;
                  });

                return { id, hold: row.hold, v, tests };
              });

              setSamples(mapped);
            } catch {
              /* non-fatal — sample grids stay empty; user can still edit */
            }
          };

          await Promise.all([
            loadChild<ReceivedDateRowDto>('received-dates', mapReceivedDateRow, 'receivedDates', setRdLoaded),
            loadChild<NoteRowDto>('notes', mapNoteRow, 'notes', setNotesLoaded),
            loadChild<SccDto>('scc', mapSccRow as (r: SccDto) => unknown, 'sccRecords', setSccLoaded, true),
            loadChild<ItemChargeRowDto>('item-charges', mapItemChargeRow, 'itemCharges', setIcLoaded),
            loadChild<AttachmentRowDto>('attachments', mapAttachmentRow, 'attachments', setAttLoaded),
            loadChild<ImageRowDto>('images', mapImageRow, 'images', setImgLoaded),
            loadCustomRequests(),
            loadSampleLogins(),
          ]);
        } else {
          setRdLoaded(true);
          setNotesLoaded(true);
          setSccLoaded(true);
          setIcLoaded(true);
          setAttLoaded(true);
          setImgLoaded(true);
        }
      } catch (e) {
        if (dead) return;
        const msg = e instanceof Error ? e.message : 'Failed to load registration.';
        setDetailError(msg);
        notify.error('Failed to load Sample Registration detail.');
      } finally {
        if (!dead) setDetailLoading(false);
      }
    };
    void readDetail();
    return () => { dead = true; };
  }, [jobId, notify, nowLocal, registrationType, user]);

  // Lookups reload when the Sample Matrix (gates Test) OR the Client changes —
  // project / contact / quote / collector are CLIENT-SCOPED server-side, so the
  // lists are simply empty until a client is chosen (legacy `1=2`).
  useEffect(() => {
    void loadLookups(header.matrices[0], header.client, header.clientOid);
  }, [header.matrices, header.client, header.clientOid, loadLookups]);

  // Due Date = Received Date + TAT hours (working days/hours, skipping
  // weekends + holidays).  Auto-calculates when TAT or ReceivedDate changes;
  // the user can then manually adjust via the editable datetime field.
  useEffect(() => {
    const hours = tatHoursMap[header.tat];
    if (hours === undefined) return;
    const isSampling = registrationType === 'Sampling';
    const due = resolveDueDate(header.receivedDate, hours, isSampling, holidaySet);
    if (due && due !== header.dueDate) patch({ dueDate: due });
  }, [header.tat, header.receivedDate, tatHoursMap, holidaySet, registrationType, header.dueDate, patch]);

  // Clearing the Sample Matrix re-gates (and clears) Test + No Of Samples.
  useEffect(() => {
    if (header.matrices.length > 0) return;
    if (header.testTemplate.length > 0 || header.noOfSamples) {
      patch({ testTemplate: [], noOfSamples: '1' });
    }
  }, [header.matrices, header.testTemplate.length, header.noOfSamples, patch]);

  const matrixMap = useMemo(() => {
    const m: Record<string, string> = {};
    const obl = formLookups.matrix?.oidByLabel;
    if (obl) {
      for (const [name, oid] of Object.entries(obl)) {
        // Oid → name (lowercase — .NET Guid.ToString() returns lowercase)
        m[oid.toLowerCase()] = name;
        // name → name (new samples store the display name, not the Oid)
        m[name] = name;
      }
    }
    return m;
  }, [formLookups.matrix]);

  /** The header-level test template as test records (Custom Request rows).
   *  testTemplate entries are the "TestName|MethodNumber" composite — split back
   *  to the bare name + method so the samples/tests grid shows them cleanly. */
  const templateTests = useMemo(
    () => header.testTemplate.map(composite => {
      const bar = composite.indexOf('|');
      const name = bar >= 0 ? composite.slice(0, bar) : composite;
      const method = header.testTemplateMethods[composite] ?? (bar >= 0 ? composite.slice(bar + 1) : '');
      const testRow = lookups.test.rows.find(r => r[0] === name && r[1] === method)
                   ?? lookups.test.rows.find(r => r[0] === name);
      const component = testRow?.[3] ?? '';
      return mkTest(name, method, undefined, '', component);
    }),
    [header.testTemplate, header.testTemplateMethods, lookups.test.rows],
  );

  // ── Counts driving the stepper + flow bar ────────────────────────────────
  const counts = useMemo(() => {
    if (samples.length > 0) {
      return {
        samples: samples.length,
        tests: samples.reduce((a, s) => a + s.tests.length, 0),
        containers: samples.reduce((a, s) => a + (Number.parseInt(s.v.Qty ?? '1', 10) || 1), 0),
      };
    }
    // Preview before samples are generated: No Of Samples × the test template.
    const n = Math.max(1, Number.parseInt(header.noOfSamples, 10) || 1);
    return { samples: n, tests: n * header.testTemplate.length, containers: n };
  }, [samples, header.noOfSamples, header.testTemplate.length]);

  const submitted = /^submitted/i.test(header.status);

  // ── Step state — computed from the DATA, never a static wizard ────────────
  const islandSteps = useMemo((): IslandStep[] => {
    const done = [
      !isNew,                                                                   // Registration saved
      samples.length > 0,                                                       // Samples created
      samples.length > 0 && samples.every(s => s.tests.length > 0),              // every sample has a test
      samples.length > 0 && samples.every(s => s.tests.every(t => !!t.bottle)),  // bottles assigned
      !isNew,                                                                   // record persisted
      submitted,                                                                // submitted
    ];
    // On the Samples step the Sample Login tab decides the active node.
    const activeIdx: number = step === 1
      ? (slTab === 'testview' ? 2 : slTab === 'containers' ? 3 : 1)
      : step;
    return STEPS.map((label, i) => ({
      id: String(i),
      label,
      state: i === activeIdx ? 'active' : done[i] ? 'done' : 'todo',
    }));
  }, [isNew, samples, submitted, step, slTab]);

  // ── Guards ────────────────────────────────────────────────────────────────
  /** Client + Sample Matrix are the only required fields (decision 2026-07-18). */
  const missingRequired = useMemo(() => {
    const out: { label: string; id: string }[] = [];
    if (!header.client) out.push({ label: 'Client', id: 'sr-f-client' });
    if (header.matrices.length === 0) out.push({ label: 'Sample Matrix', id: 'sr-f-matrix' });
    return out;
  }, [header.client, header.matrices.length]);

  const warnMissing = useCallback(async (message: string) => {
    setTriedSave(true);
    await warnDialog(message, { title: 'Required Fields Missing', list: missingRequired.map(m => m.label) });
    // After "OK", flash the offending fields and scroll the first into view so
    // the user can FIND them on the long Registration form.
    setStep(0);
    setFlash(new Set(missingRequired.map(m => m.id)));
    setTimeout(() => {
      const first = missingRequired[0];
      if (first) document.getElementById(first.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    setTimeout(() => setFlash(new Set()), 4000);
  }, [missingRequired]);

  /** Generate the sample rows from No Of Samples (top up, never drop rows).
   *  For saved jobs (jobOid set), the DB-loaded samples are the truth — do NOT
   *  pad extra rows from the registration header's noOfSamples. */
  const ensureSamples = useCallback((job: string) => {
    setSamples(prev => {
      const next = prev.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
      // Only pad from noOfSamples for NEW (unsaved) jobs.  Saved jobs already
      // have their samples loaded from the SampleLogIn table — noOfSamples
      // on the registration header is informational, not prescriptive.
      if (!jobOid) {
        const want = Math.max(1, Number.parseInt(header.noOfSamples, 10) || 1);
        while (next.length < want) {
          const s: SrSample = {
            id: sampleId(job, next.length + 1),
            hold: false,
            v: {
              Qty: '1',
              SampleMatrix: header.matrices[0] ?? '',
              ReceivedDate: tabs.receivedDates[0]?.date ?? header.receivedDate,
              EnteredBy: user,
              EnteredDate: toDateInput(nowLocal),
            },
            tests: templateTests.map(cloneTest),
          };
          autoBottles(s);
          next.push(s);
        }
      }
      renumber(next, job);
      return next;
    });
  }, [jobOid, header.noOfSamples, header.matrices, header.receivedDate, tabs.receivedDates, templateTests, user, nowLocal]);

  /**
   * Leaving Registration for any sub-view requires a saved job: the prototype
   * (and legacy) auto-save here so the generated Sample IDs are real and stable.
   * TODO(AI-29131): this must call the save endpoint (race-safe Job ID via
   * ISequentialIdGenerator — §14a Rule B) instead of minting client-side.
   */
  const prepareForSubViews = useCallback(async (): Promise<boolean> => {
    if (currentJobId) { ensureSamples(currentJobId); return true; }
    if (missingRequired.length > 0) {
      await warnMissing('Fill these before adding samples:');
      return false;
    }
    if (!Number.parseInt(header.noOfSamples, 10)) patch({ noOfSamples: '1' });
    const job = provisionalJobId();
    setCurrentJobId(job);
    patch({ jobId: job, status: 'Pending Submission' });
    ensureSamples(job);
    notify.success(`Job ${job} created — status Pending Submission.`);
    return true;
  }, [currentJobId, missingRequired.length, header.noOfSamples, ensureSamples, warnMissing, patch, notify]);

  const goToStep = useCallback(async (next: StepIndex) => {
    if (next >= 1 && !(await prepareForSubViews())) return;
    setStep(next);
    if (next === 1) setSlTab('login');
    if (next === 2) setSlTab('testview');
    if (next === 3) setSlTab('containers');
    window.scrollTo(0, 0);
  }, [prepareForSubViews]);

  // ── Save / Submit ─────────────────────────────────────────────────────────

  /**
   * The ONE persistence point — POST (create) or PUT (update) to the API.
   * The endpoint owns: the race-safe Job ID (ISequentialIdGenerator), the audit
   * row (IAuditTrailService), the bottle grouping, and the TAT → Due Date derivation.
   */
  const persist = useCallback(async (): Promise<string> => {
    // The header + child collections share ONE atomic Save. Every FK the API
    // parses as a Guid MUST be resolved from display → Oid via `oidByLabel`
    // (populated by mapLookupRows + employee-lookup fetch). Sending a display
    // name like "ACME Labs" for @ClientName makes ParseGuid return NULL, and
    // the SP throws "Client is required" — same for every other FK column.
    // Belt-and-braces resolution: return the FK Oid if the lookup SP surfaced
    // one (Col5), else fall back to the raw display name so the backend writer
    // can resolve it via Dapper (SampleRegistrationWriter.ResolveFkAsync). This
    // means save works even when the lookup SP hasn't been redeployed with the
    // Col5 change — the C# path handles either.
    const oid = (kind: SrLookupKey, label: string | undefined): string | null => {
      if (!label) return null;
      const map = formLookups[kind]?.oidByLabel;
      return map?.[label] ?? label;
    };
    const oidList = (kind: SrLookupKey, labels: readonly string[]): string | null => {
      const xs = labels.map(l => oid(kind, l)).filter((x): x is string => !!x);
      return xs.length ? xs.join('; ') : null;
    };

    // Normalise a datetime-local value so the API reliably keeps the TIME:
    // "YYYY-MM-DDTHH:mm" (16 chars) → append ":00" seconds so System.Text.Json
    // parses the full timestamp; a date-only value is promoted to T00:00:00.
    const wireDateTime = (v: string | undefined): string | null => {
      if (!v) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00`;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return `${v}:00`;
      return v;
    };

    // Header-level test template as Oids (persisted into SampleCheckinTest).
    const headerTestOids = header.testTemplate
      .map(t => oid('test', t))
      .filter((x): x is string => !!x);

    // Phase 1 child payloads persisted by AI_SampleRegistration_SaveChildren:
    //   · samples[]      → dbo.SampleLogIn
    //   · headerTests[]  → dbo.SampleCheckinTest (M:N junction on the header)
    //   · sampleTests[]  → dbo.SampleParameter (per-sample per-test)
    // `key` is the client-side stable id (SrSample.id / SrTest.key) — server
    // uses it only to join sampleTests to the freshly-minted SampleLogIn.Oid.
    const headerMatrixOid = oid('matrix', header.matrices[0]);
    // Helper: check if a value is already a GUID (stored by the dynamic config
    // FK picker) so we can send it as-is without a display-name→Oid resolution.
    const isGuid = (v?: string | null) =>
      !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

    const samplesBody = samples.map((s, i) => ({
      key:            s.id,
      sampleNo:       i + 1,
      // SampleMatrix: dynamic config stores the OID in v['SampleMatrix'] (the
      // FIELD_ID_TO_V_KEY remapping maps VisualMatrix → SampleMatrix). The static
      // SL_COLS path stores the display name there, so we also check isGuid.
      // Legacy key v['VisualMatrix'] is kept as a final fallback for any old
      // in-flight data that predates the remap.
      matrix:         isGuid(s.v['SampleMatrix']) ? s.v['SampleMatrix']
                    : isGuid(s.v['VisualMatrix'])  ? s.v['VisualMatrix']
                    : headerMatrixOid,
      // SampleType: dynamic config stores the OID directly in v['SampleType'].
      sampleType:     isGuid(s.v['SampleType']) ? s.v['SampleType'] : null,
      qty:            Number(s.v.Qty) || 1,
      // Collector: dynamic config stores the OID in v['Collector'];
      // static fallback stores display name that must be resolved via oid().
      collector:      isGuid(s.v['Collector']) ? s.v['Collector'] : (oid('collector', s.v.Collector) ?? null),
      collectDate:    s.v.DateCollected || s.v.CollectDate || null,
      collectTime:    s.v.TimeCollected || s.v.CollectTimeDisplay || null,
      clientSampleId: s.v.SampleName || s.v.ClientSampleID || null,
      description:    s.v.Description || null,
      comment:        s.v.Remark || null,
      systemType:     isGuid(s.v['SystemType'])   ? s.v['SystemType']   : null,
      qcCategory:     isGuid(s.v['QCCategory'])   ? s.v['QCCategory']   : null,
      hold:           s.hold ?? false,
      subOut:         s.v['SubOut'] === '1' || s.v['SubOut'] === 'true',
      // Additional configurable columns persisted by SaveChildren SP
      siteName:       isGuid(s.v['SiteName']) ? s.v['SiteName'] : null,
      address:        s.v.Address || null,
      keyMap:         s.v.KeyMap || null,
      samplingAddress: s.v.SamplingAddress || null,
      samplePointID:  s.v.SamplePointID || null,
      samplePointType: s.v.SamplePointType || null,
      pwsid:          isGuid(s.v['PWSID']) ? s.v['PWSID'] : null,
      waterSource:    s.v.WaterType || null,
      rejectionCriteria: isGuid(s.v['RejectionCriteria']) ? s.v['RejectionCriteria'] : null,
      repeatLocation: isGuid(s.v['RepeatLocation']) ? s.v['RepeatLocation'] : null,
      department:     isGuid(s.v['Department']) ? s.v['Department'] : null,
      sampleStatus:   isGuid(s.v['SampleStatus']) ? s.v['SampleStatus'] : null,
      parentSampleID: s.v.ParentSampleID || null,
      parentSampleDate: s.v.ParentSampleDate || null,
      sysSampleCode:  s.v.SysSampleCode || null,
      projectNumber:  s.v.ProjectNumber || null,
      plannedLatitude: s.v.PlannedLatitude || null,
      plannedLongitude: s.v.PlannedLongitude || null,
      chlorineFree:   s.v['FreeColorine(mg/L)'] || null,
      chlorineTotal:  s.v['TotalChlorine(mg/L)'] || null,
      flowRate:       s.v.FlowRate || null,
      volume:         s.v.Volume || null,
      time:           s.v.Time || null,
      startFlow:      s.v.StartFlow || null,
      endFlow:        s.v.EndFlow || null,
      timeStart:      s.v.TimeStart || null,
      timeEnd:        s.v.TimeEnd || null,
      collectorPhone: s.v.CollectorPhone || null,
      samplingEvent:  s.v.SamplingEvent || null,
      alternativeStation: s.v.AlternativeStation || null,
      excludeInvoice: s.v['ExcludeInvoice'] === '1' || s.v['ExcludeInvoice'] === 'true',
      notReport:      s.v['NotReport'] === '1' || s.v['NotReport'] === 'true',
      complianceProject: s.v['ComplianceProject'] === '1' || s.v['ComplianceProject'] === 'true',
      dueDate:        s.v.DueDate || null,
      restTime:       s.v.RestTime || null,
      ph:             s.v.PH || null,
      dissolvedOxygen: s.v.DO || null,
      standardVol:    s.v.StandardVol || null,
      absorptionvol:  s.v.Absorptionvol || null,
      trafficFlowBig: s.v.TrafficFlowBigVechile || null,
      trafficFlowMiddle: s.v.TrafficFlowMiddleVechile || null,
      trafficFlowSmall: s.v.TrafficFlowSmallVechile || null,
      receivedDate:   s.v.ReceivedDate || null,
    }));
    // When samples state is empty (user hasn't navigated to the Login step yet),
    // synthesise sample stubs from noOfSamples + templateTests so Custom Request
    // edits are still included in the payload.  The sampleKey format
    // (JobID + '-' + zero-padded ordinal) matches the SP's ELSE branch that
    // populates @SampleMap from existing SampleLogIn rows when @SamplesJson is null.
    const samplesForTests: Pick<SrSample, 'id' | 'tests'>[] =
      samples.length > 0
        ? samples
        : currentJobId && templateTests.length > 0
          ? Array.from(
              { length: Math.max(1, Number.parseInt(header.noOfSamples, 10) || 1) },
              (_, i) => ({ id: sampleId(currentJobId, i + 1), tests: templateTests }),
            )
          : [];
    // Build one SampleParameter row per SELECTED parameter per sample.
    // When the user has customised parameters (t.params is non-null),
    // only selected parameters are sent. Unselected ones are omitted,
    // which the SP treats as a delete (replace-all semantics).
    // Each entry carries testparameterOid (dbo.Testparameter.Oid) so
    // the SP can insert directly without resolution; testOid
    // (TestMethod.Oid) is kept as a fallback for the auto-seed path.
    const sampleTestsBody = samplesForTests.flatMap(s =>
      s.tests.flatMap(t => {
        const composite = t.method ? `${t.name}|${t.method}` : t.name;
        const testOid = oid('test', composite);
        if (!testOid) return [];
        const cr = customRequest[t.name];
        const statusIdx = SAMPLE_STATUS_OPTS.indexOf(t.status ?? '');
        const base = {
          sampleKey: s.id,
          testOid,
          comp:    t.comp || null,
          hold:    cr?.hold            ?? false,
          tatOid:  cr?.tat ? oid('tat', cr.tat) ?? null : null,
          dueDate: cr?.due             ?? null,
          subOut:  t.subOut            ?? false,
          status:  statusIdx >= 0 ? statusIdx : 0,
        };
        // If parameters were customised, emit one row per selected param
        if (t.params) {
          const entries = Object.entries(t.params)
            .filter(([, on]) => on)
            .map(([pm]) => ({
              ...base,
              testparameterOid: cachedParamOid(t, pm) ?? null,
            }))
            .filter(e => e.testparameterOid);
          return entries.length > 0 ? entries : [{ ...base, testparameterOid: null }];
        }
        // All parameters selected (default) — let the SP resolve via CROSS APPLY
        return [{ ...base, testparameterOid: null }];
      }),
    );

    // Received Dates (dbo.SC_ReceivedDates) — replace-all save/edit/delete. The
    // sub-tab stores display values (by/relinq/tat/matrix/cat); the API resolves
    // them to Oids. `oid` round-trips so an existing row UPDATEs, not duplicates.
    // Guarded by rdLoaded: for an existing job we only make the sub-tab
    // authoritative (and thus able to delete rows) AFTER its rows have loaded —
    // sending null before that leaves the table untouched.
    const receivedDatesBody = rdLoaded
      ? tabs.receivedDates.map((r: SrReceivedDate) => ({
          oid: r.oid || null,
          receivedDate: wireDateTime(r.date),
          receivedBy: r.by || null,
          relinquishedBy: r.relinq || null,
          tat: r.tat || null,
          sampleMatrix: r.matrix.join('; ') || null,
          sampleCategory: r.cat.join('; ') || null,
          coolerId: r.cooler || null,
          noOfSamples: r.n || 1,
        }))
      : null;

    const body = {
      isSampling: header.registrationType === 'Pre-scheduled',
      // FK fields — every one goes through display→Oid resolution. Client
      // prefers the exact captured Oid (unambiguous for duplicate names).
      clientName:      header.clientOid || oid('client', header.client),
      clientContact:   oid('contact',   header.clientContact),
      invoiceContact:  oid('contact',   header.invoiceContact),
      quoteId:         oid('quote',     header.quoteId),
      po: header.poNumber || null,
      paymentStatus:   oid('payment',   header.paymentStatus),
      manifestNumbers: header.manifestNumber ? Number(header.manifestNumber) || null : null,
      projectId:       oid('project',   header.projectId),
      projectCategory: oid('category',  header.projectCategory),
      projectManager:  oid('pm',        header.projectManager),
      permitLibrary:   oid('permit',    header.permit),
      cocId:           oid('cocid',     header.cocId),
      jobInactivation: oid('inact',     header.jobInactivation),
      receivedDate: wireDateTime(header.receivedDate),
      receivedBy:      oid('employee',  header.receivedBy),
      dateCollected: wireDateTime(header.dateCollected),
      timeCollected: header.timeCollected || null,
      // Multi-select FK fields — resolve every entry, join with '; '
      relinquishedBy:  oidList('relinq',         header.relinquishedBy),
      collector:       oidList('collector',      header.collector),
      dateExpect: header.dateExpect || null,
      sampleMatrices:  oidList('matrix',         header.matrices),
      sampleCategory:  oidList('sampleCategory', header.sampleCategory),
      test:            oidList('test',           header.testTemplate),
      numberOfSample: Number(header.noOfSamples) || 1,
      coolerId: header.coolerId || null,
      remark: header.remark || null,
      tat:             oid('tat',       header.tat),
      dueDate: header.dueDate || null,
      // ReportTemplate is a plain string column — no FK, no Oid resolution.
      reportTemplate: header.reportTemplate || null,
      eddTemplate:     oid('eddtpl',    header.eddTemplate),
      // EDDFormat is the legacy Samplecheckin.EDDFormat enum (INT). The picker
      // stores the DISPLAY label ("Export to CSV"); map it to the enum value.
      eddFormat: eddFormatToInt(header.eddFormat),
      reportDeliveryAddress: header.reportDeliveryAddress || null,
      reportDeliveryMethod: header.reportDeliveryMethod || null,
      // Phase 1 child collections
      samples:     samplesBody.length ? samplesBody : null,
      headerTests: headerTestOids.length ? headerTestOids : null,
      sampleTests: sampleTestsBody.length ? sampleTestsBody : null,
      // Received Dates sub-tab (save/edit/delete via replace-all)
      receivedDates: receivedDatesBody,

      // ── Additional child tab collections (guarded by loaded flags) ──
      notes: notesLoaded
        ? tabs.notes.map((n: SrNote) => ({
            oid: n.oid || null,
            title: n.title || null,
            html: n.html || null,
            plainText: n.plainText || null,
            sort: n.sort,
            sourceId: n.sourceId || null,
            noteSource: n.noteSource || null,
          }))
        : null,

      scc: sccLoaded && tabs.sccRecords.length > 0
        ? (() => {
            const s = tabs.sccRecords[0]!;
            return {
              oid: s.oid || null,
              checkInBy: s.by || null,
              checkInDate: wireDateTime(s.date),
              visualMatrix: s.matrix || null,
              temperature: s.temp || null,
              correctedTemperature: s.corr || null,
              thermometerId: s.thermo || null,
              samplePH: s.ph || null,
              phPaperId: s.paper || null,
              receivedDate: s.recv || null,
              checkPoints: SCC_POINTS.map((p, i) => ({
                checkPointName: p[0],
                isYes: s.points[i] === 'yes',
                isNo: s.points[i] === 'no',
                isNA: s.points[i] === 'na',
                description: s.descriptions[i] ?? '',
              })),
              comments: s.comments.map(c => ({ comment: c.text })),
            };
          })()
        : (sccLoaded ? null : undefined),

      itemCharges: icLoaded
        ? tabs.itemCharges.map((ic: SrItemCharge) => ({
            oid: ic.oid || null,
            itemPrice: ic.itemPriceOid || null,   // send FK Oid, not display name
            description: ic.desc || null,
            unitPrice: ic.price,
            qty: ic.qty,
            amount: ic.amount,
            finalAmount: ic.final,
            npUnitPrice: ic.npPrice,
            discount: ic.disc,
            remark: ic.remark || null,
          }))
        : null,

      attachments: attLoaded
        ? tabs.attachments.map((a: SrAttachment) => ({
            oid: a.oid || null,
            name: a.name || null,
            category: Number.isFinite(Number(a.cat)) ? Number(a.cat) : 5,
            operator: a.opOid || a.op || null,
            fileDataOid: a.fileDataOid || null,
            date: wireDateTime(a.date),
            comment: a.comment || null,
            receivedDate: a.recv || null,
          }))
        : null,

      images: imgLoaded
        ? tabs.images.map((img: SrImage) => ({
            oid: img.oid || null,
            name: img.name || null,
            comment: img.comment || null,
            sort: img.sort,
          }))
        : null,
    };

    let res: { data: { success: boolean; data: { oid: string; jobId: string; action: string } } };
    if (currentJobId && jobOid) {
      res = await api.put(`/api/v1/sample-registration/${jobOid}`, body);
    } else {
      res = await api.post('/api/v1/sample-registration', body);
    }
    if (!res.data.success) {
      // Surface the real server-side error so the user (and AI_ExceptionLog
      // triage) can act on it. Same shape used by every AI controller:
      // { success:false, error: { code, message } }.
      const err = (res.data as unknown as { error?: { message?: string } }).error;
      throw new Error(err?.message ?? 'Save failed');
    }
    const savedOid = res.data.data.oid;
    if (savedOid) setJobOid(savedOid);

    // Re-fetch all child tabs so their freshly-minted server Oids populate
    // the sub-tabs — without this a SECOND save would re-INSERT (duplicate)
    // the same rows instead of UPDATE-ing them.
    if (savedOid) {
      const oidEnc = encodeURIComponent(savedOid);
      const refetch = async <T,>(
        path: string, map: (r: T) => unknown, tabKey: keyof SrTabState,
        setLoaded: (v: boolean) => void, sent: unknown, isSingle?: boolean,
      ) => {
        if (sent === null || sent === undefined) return;
        try {
          const r = await api.get(`/api/v1/sample-registration/${oidEnc}/${path}`);
          const raw = (r.data as ApiSuccess<T | T[]>).data;
          if (isSingle) {
            const mapped = raw ? [map(raw as T)] : [];
            setTabs(t => ({ ...t, [tabKey]: mapped }));
          } else {
            const arr = (raw as T[] | null) ?? [];
            setTabs(t => ({ ...t, [tabKey]: arr.map(map) }));
          }
          setLoaded(true);
        } catch { /* non-fatal; server logged it */ }
      };
      await Promise.all([
        refetch<ReceivedDateRowDto>('received-dates', mapReceivedDateRow, 'receivedDates', setRdLoaded, receivedDatesBody),
        refetch<NoteRowDto>('notes', mapNoteRow, 'notes', setNotesLoaded, body.notes),
        refetch<SccDto>('scc', mapSccRow as (r: SccDto) => unknown, 'sccRecords', setSccLoaded, body.scc, true),
        refetch<ItemChargeRowDto>('item-charges', mapItemChargeRow, 'itemCharges', setIcLoaded, body.itemCharges),
        refetch<AttachmentRowDto>('attachments', mapAttachmentRow, 'attachments', setAttLoaded, body.attachments),
        refetch<ImageRowDto>('images', mapImageRow, 'images', setImgLoaded, body.images),
      ]);
    }
    return res.data.data.jobId;
  }, [currentJobId, jobOid, header, samples, customRequest, formLookups, rdLoaded, notesLoaded,
      sccLoaded, icLoaded, attLoaded, imgLoaded, tabs]);

  const doSave = useCallback(async (after: 'stay' | 'close' | 'new') => {
    setTriedSave(true);
    if (missingRequired.length > 0) {
      await warnMissing('Please fill in the following fields before saving:');
      return;
    }
    const wasNew = isNew;
    let mintedJob = currentJobId ?? '';
    await runSave({
      formName: SAMPLE_REGISTRATION_FORM,
      auditAction: wasNew ? 'create' : 'edit',
      save: async () => { mintedJob = await persist(); },
      // A milestone success (§13) when the save mints a NEW Job ID.
      successDialog: wasNew
        ? () => ({
          type: 'success' as const,
          title: 'Job Created Successfully',
          idLabel: 'Job ID',
          idValue: mintedJob,
          rows: [
            { label: 'Status', value: 'Pending Submission' },
            { label: 'Client', value: header.client },
            { label: 'Samples', value: String(counts.samples) },
          ],
          note: 'Job saved. Samples, tests, and per-sample test assignments persisted (Phase 1).',
          actions: [
            { label: '← Keep Editing', value: 'stay' },
            { label: 'Sample Login →', value: 'login' },
            { label: after === 'new' ? 'Register Next →' : after === 'close' ? 'Go to List →' : 'OK', primary: true, value: 'next' },
          ],
        })
        : undefined,
      onSaved: () => {
        dirtyRef.current = false;
        setCurrentJobId(mintedJob);
        patch({ jobId: mintedJob, status: submitted ? header.status : 'Pending Submission' });
        onSaved();
        if (after === 'close') { onClose(); return; }
        if (after === 'new') {
          setHeader(seedHeader(null, registrationType, nowLocal, user, undefined));
          setSamples([]);
          setTabs(seedTabs(nowLocal, user));
          setCustomRequest({});
          setCurrentJobId(null);
          setJobOid(null);
          setRdLoaded(true);   // fresh job — nothing to load
          setNotesLoaded(true);
          setSccLoaded(true);
          setIcLoaded(true);
          setAttLoaded(true);
          setImgLoaded(true);
          setTriedSave(false);
          setStep(0);
        }
      },
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }, [
    missingRequired.length, warnMissing, isNew, currentJobId, runSave, persist, header,
    counts.samples, submitted, patch, onSaved, onClose, registrationType, nowLocal, user, notify,
  ]);

  /** Reset Job ID (prototype `↺ Reset Job ID`) — drops the minted id so the
   *  record returns to Autogenerate / Pending Saving.  Confirmed, because it
   *  discards the identity the samples were numbered against. */
  const resetJobId = useCallback(async () => {
    if (!currentJobId) { notify.warning('No Job ID to reset — this registration has not been saved yet.'); return; }
    const ok = await confirm({
      title: 'Reset Job ID',
      message: `Reset Job ID ${currentJobId}? The registration returns to Pending Saving and its samples are re-numbered on the next save.`,
      tone: 'danger',
    });
    if (!ok) return;
    setCurrentJobId(null);
    patch({ jobId: '', status: 'Pending Saving' });
    notify.success('Job ID reset.');
  }, [currentJobId, confirm, patch, notify]);

  const doSubmit = useCallback(async () => {
    if (!currentJobId || !jobOid) {
      notify.warning('Save the registration first, then click Submit.');
      return;
    }
    const ok = await confirm({
      title: 'Submit registration',
      message: `Submit job ${currentJobId}? It is released to Sample Receipt Notification.`,
    });
    if (!ok) return;
    try {
      const res = await api.post(`/api/v1/sample-registration/${jobOid}/submit`);
      const data = res.data?.data ?? {};
      patch({ status: data.newStatus ?? 'Submitted' });
      onSaved();
      notify.success('Submit completed successfully.');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? 'Submit failed. Please try again.';
      notify.error(msg);
    }
  }, [currentJobId, jobOid, confirm, patch, onSaved, notify]);

  // ── Lookup quick-add ──────────────────────────────────────────────────────
  const addLookupRow = (kind: SrLookupKey, row: string[]) => {
    setLookups(l => ({ ...l, [kind]: { cols: l[kind].cols, rows: [row, ...l[kind].rows] } }));
    // Select it into the field that opened the dialog (prototype parity).
    if (newLookupField) {
      const value = row[0] ?? '';
      const p: Partial<SrHeader> = { [newLookupField]: value } as Partial<SrHeader>;
      if (kind === 'client') { p.clientAddress = row[1] ?? ''; p.clientPhone = row[2] ?? ''; }
      if (kind === 'project') { p.projectName = row[1] ?? ''; p.projectLocation = row[2] ?? ''; }
      patch(p);
    }
    setNewLookupKind(null);
    setNewLookupField(null);
    notify.success(`${row[0]} added and selected.`);
  };

  const openNewLookup = (kind: SrLookupKey) => {
    setNewLookupKind(kind);
    setNewLookupField(FIELD_FOR_LOOKUP[kind] ?? null);
  };

  const confirmAction = (title: string, message: string, run: () => void, danger?: boolean) => {
    void confirm({ title, message, tone: danger ? 'danger' : undefined }).then(ok => { if (ok) run(); });
  };

  // ── Prev / next record ────────────────────────────────────────────────────
  const idx = currentJobId ? siblingIds.indexOf(currentJobId) : -1;
  const prevId = idx > 0 ? siblingIds[idx - 1] : undefined;
  const nextId = idx >= 0 && idx < siblingIds.length - 1 ? siblingIds[idx + 1] : undefined;

  const recordLabel = currentJobId ?? `New (${header.registrationType})`;
  const charge = useMemo(() => chargeRows(samples), [samples]);

  // ── Action bar ────────────────────────────────────────────────────────────
  // Plain functions invoked inline ({saveTrio()}), not inner components — a
  // component declared in a render body remounts its subtree every render.
  const saveTrio = () => (
    <>
      <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')}>
        <span className="tb-i">💾</span> Save
      </button>
      <button className="ca-btn" onClick={() => void doSave('close')}>
        <span className="tb-i">💾</span> Save and Close
      </button>
      <button className="ca-btn" onClick={() => void doSave('new')}>
        <span className="tb-i">🗎</span> Save and New
      </button>
      <span className="sr-sdiv" />
      <button className="ca-btn" onClick={() => void guardedExit(onClose)}><span className="tb-i">↩︎</span> Cancel</button>
    </>
  );

  const stepArrows = () => (
    <>
      <button className="sr-navcirc" title="Back" aria-label="Back" disabled={step === 0}
        onClick={() => void goToStep(Math.max(0, step - 1) as StepIndex)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 5 8 12 15 19" />
        </svg>
      </button>
      <button className="sr-navcirc" title="Next" aria-label="Next"
        disabled={step === 5 || (step === 4 && !currentJobId)}
        onClick={() => void goToStep(Math.min(5, step + 1) as StepIndex)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 5 16 12 9 19" />
        </svg>
      </button>
    </>
  );

  return (
    /* `sr-detail` makes this long-form page scroll (`.bl-page` is built for list
       screens and would clip it); `sr-dlg` scopes the prototype's accent dialog
       header to every popup this view renders (quick-add, Received Date, SCC,
       Note, Item Charge, Image, Import, Project Notes) — AppDialog renders
       inline, so one class on the root covers them all without touching any
       other form. */
    <div className="bl-page sr-detail sr-dlg">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="sr-crumb">
            <a onClick={() => void guardedExit(onHome)}>Home</a>
            <span className="sr-crumb-sep">›</span>
            <span>Sample Tracking</span>
            <span className="sr-crumb-sep">›</span>
            <a onClick={() => void guardedExit(onClose)}>Sample Registration</a>
            <span className="sr-crumb-sep">›</span>
            <a onClick={() => void goToStep(0)}><b>{recordLabel}</b></a>
            {step > 0 && <><span className="sr-crumb-sep">›</span><b>{STEPS[step]}</b></>}
          </div>

          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <FormTitle
              pageId="sampreg"
              title={currentJobId ? `Sample Registration — ${currentJobId}` : 'Sample Registration'}
              auditFormName={SAMPLE_REGISTRATION_FORM}
              auditSourceId={currentJobId ?? undefined}
            />
            {/* Job chip — prototype `#dJobChip`: "⏱ Draft — not yet saved" on a new
                record, "⏱ Job {id} · saved" once it has an id.  The tooltip carries
                the honest caveat: nothing reaches the server in this design pass. */}
            <span className="loadt sr-jobchip"
              title="Design pass — the Sample Registration save endpoint is not wired yet, so nothing is written to the server.">
              ⏱ {currentJobId ? `Job ${currentJobId} · saved` : 'Draft — not yet saved'}
            </span>
            {/* Project Notes — prototype `.notelink`, opens the job's notes. */}
            <button className="notelink" onClick={() => setNotesOpen(true)}
              title="Project Notes for this job">
              📌 Project Notes ({tabs.notes.length})
            </button>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="sr-navcirc" style={{ width: 30, height: 30 }} title="Previous record"
                disabled={!prevId} onClick={() => prevId && void guardedExit(() => onNavigate(prevId))}>‹</button>
              <button className="sr-navcirc" style={{ width: 30, height: 30 }} title="Next record"
                disabled={!nextId} onClick={() => nextId && void guardedExit(() => onNavigate(nextId))}>›</button>
            </div>
          </div>

          {/* The ONE stepper — portalled into the AppShell top bar.  The prototype
              deliberately HIDES the step hint app-wide (`.hint{display:none!important}`
              — "not very useful once the user knows what to do, the stepper is good
              enough", 2026-07-18), so the banner is not rendered.  `stepHints` below
              keeps the wording ready if it is ever switched back on. */}
          <TopbarStepper steps={islandSteps} />
        </div>
      </div>

      <div className="bl-grid-area">
        {detailLoading && (
          <div className="sr-note" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <LoaderSymbol size={16} />
            Loading registration details…
          </div>
        )}
        {detailError && (
          <div className="sr-warnbox" style={{ marginBottom: 10 }}>
            Could not load this job from the server. You can refresh and try again.
          </div>
        )}
        {/* Action bar — form-specific LEFT, the Save set RIGHT (XAF canonical).
            Shown on the Sample Registration step (0) only. Hidden on Sample Login
            (step 1) and the focused Tests / Containers steps (2, 3), which carry
            their own Save changes / Cancel changes actions. */}
        {step === 0 && (
          <div className="sr-savebar">
            <div style={{ position: 'relative' }}>
              <button className="ca-btn" onClick={() => setReportsOpen(o => !o)}>
                <span className="tb-i tb-i-blue">📄</span> Reports ▾
              </button>
              {reportsOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setReportsOpen(false)} />
                  <div className="sr-colfind-menu" style={{ top: 34, left: 0, minWidth: 170, zIndex: 41 }}>
                    {['COC Report', 'Pre Invoice'].map(r => (
                      <button key={r} className="sr-colfind-item" onClick={() => {
                        setReportsOpen(false);
                        // TODO(AI-29131): render via IPdfReportGenerator<T> (standing rule 8).
                        notify.info(`${r} preview is not wired yet.`);
                      }}>{r}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* pH Check — always present (prototype); opens Sample Container pH
                Check when the host wires it, otherwise says so rather than vanish. */}
            <button className="ca-btn" title="Open Sample Container pH Check"
              onClick={() => onOpenPhCheck
                ? onOpenPhCheck()
                : notify.info('Sample Container pH Check is not part of this pass.')}>
              <span className="tb-i tb-i-teal">🧫</span> pH Check
            </button>
            <button className="ca-btn" onClick={() => notify.success('Invoice recalculated.')}>
              <span className="tb-i tb-i-blue">🧮</span> Recalculate Invoice
            </button>
            {/* Reset Job ID — prototype `↺ Reset Job ID`: clears the minted id so
                the record goes back to Autogenerate / Pending Saving. */}
            <button className="ca-btn" title="Reset Job ID" onClick={() => void resetJobId()}>
              <span className="tb-i tb-i-grey">↺</span> Reset Job ID
            </button>
            {/* Submit is hidden until the record has an id (prototype: #submitBtn
                is hidden on a New registration, shown on an existing one). */}
            {!isNew && (
              <button className="ca-btn" onClick={() => void doSubmit()} disabled={submitted}
                title={submitted ? 'Already submitted' : 'Submit — status → Registration Submitted'}>
                <span className="tb-i tb-i-green">📨</span> Submit
              </button>
            )}
            <span className="sr-savebar-spacer" />
            {saveTrio()}
            <span className="sr-sdiv" />
            {stepArrows()}
          </div>
        )}

        {/* ── Step bodies ─────────────────────────────────────────────────── */}
        {step === 0 && (
          <>
            <RegistrationForm
              header={header}
              patch={patch}
              lookups={formLookups}
              onNewLookup={openNewLookup}
              onImport={setImportKind}
              onWarn={m => notify.warning(m)}
              flash={flash}
              triedSave={triedSave}
              statusNameMap={statusNameMap}
              savedJob={!!jobOid}
            />
            <RegistrationTabs
              header={header}
              jobId={currentJobId ?? ''}
              user={user}
              now={nowLocal}
              tabs={tabs}
              setTabs={setTabs}
              templateTests={templateTests}
              customRequest={customRequest}
              setCustomRequest={setCustomRequest}
              tatHoursMap={tatHoursMap}
              holidaySet={holidaySet}
              registrationType={registrationType}
              lookups={formLookups}
              onWarn={m => notify.warning(m)}
              onInfo={m => notify.info(m)}
              onSuccess={m => notify.success(m)}
              confirmDelete={(what, run) =>
                confirmAction('Delete', `Delete ${what}? This cannot be undone.`, run, true)}
            />
          </>
        )}

        {(step === 1 || step === 2 || step === 3) && (
          <SampleLoginView
            jobId={currentJobId ?? ''}
            jobOid={jobOid}
            primaryMatrixOid={
              // Resolve the first selected matrix name → OID for New Mode config fetch.
              (header.matrices[0] != null
                ? (formLookups.matrix?.oidByLabel?.[header.matrices[0]] ?? null)
                : null)
            }
            clientOid={header.clientOid ?? null}
            samples={samples}
            setSamples={setSamples}
            tab={slTab}
            setTab={t => {
              setSlTab(t);
              setStep(t === 'testview' ? 2 : t === 'containers' ? 3 : 1);
            }}
            active={active}
            setActive={setActive}
            onBackToRegistration={() => void goToStep(0)}
            onWarn={m => notify.warning(m)}
            onInfo={m => notify.info(m)}
            onSuccess={m => notify.success(m)}
            confirmAction={confirmAction}
            onNewCollector={() => { setNewLookupKind('collector'); setNewLookupField(null); }}
            actions={
              <>
                <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')}>Save</button>
                <button className="ca-btn" onClick={() => void doSave('close')}>Save and Close</button>
                <button className="ca-btn" onClick={() => void guardedExit(onClose)}>Cancel</button>
              </>
            }
            testRows={formLookups.test?.rows ?? []}
            groupChildren={groupChildren}
            matrixMap={matrixMap}
          />
        )}

        {step === 4 && (
          <section className="sr-sec">
            <h3 className="sr-sec-h"><span className="sr-sec-ico">💾</span> Save</h3>
            <div className="sr-sec-b">
              <div className="sr-savebar" style={{ margin: '0 0 12px' }}>
                <span className="sr-savebar-spacer" />
                {saveTrio()}
              </div>
              <SummaryBlock header={header} jobId={currentJobId} counts={counts} charge={charge} />
              <p className="sr-note" style={{ marginTop: 10 }}>
                Click <b>Save</b> to mint the Job ID (status <b>Pending Submission</b>), then continue
                to <b>Submit</b>.
              </p>
            </div>
          </section>
        )}

        {step === 5 && (
          <section className="sr-sec">
            <h3 className="sr-sec-h"><span className="sr-sec-ico">📨</span> Submit</h3>
            <div className="sr-sec-b">
              <div className="sr-savebar" style={{ margin: '0 0 12px' }}>
                <span className="sr-savebar-spacer" />
                <button className="ca-btn ca-btn-solid" disabled={!currentJobId || submitted}
                  title={currentJobId ? undefined : 'Save the registration first'}
                  onClick={() => void doSubmit()}>
                  <span className="tb-i">📨</span> Submit
                </button>
              </div>
              {currentJobId ? (
                <p className="sr-note">
                  Job <b>{currentJobId}</b> is saved (status <b>{header.status}</b>). Click <b>Submit</b> to
                  set it <b>Submitted</b> and release it to Sample Receipt Notification.
                </p>
              ) : (
                /* Prototype-exact amber note on the Submit step (this one IS shown —
                   it is a step-body warning, not the hidden hint banner). */
                <div className="sr-warnbox">
                  ⚠ Not saved yet — go back to <b>Save</b> first. Submit is disabled until the Job ID is created.
                </div>
              )}
              <SummaryBlock header={header} jobId={currentJobId} counts={counts} charge={charge} />
            </div>
          </section>
        )}

        {/* ── Flow bar — step CTAs with live counts, Back / Next on the right ── */}
        {/* Hidden on Sample Login / Test View / Containers (steps 1-3) to give
            grids more vertical space; shown on Registration + Save + Submit. */}
        {(step === 0 || step >= 4) && <div className="sr-flowbar">
          {([[1, '🧪 Samples', counts.samples], [2, '🔬 Tests', counts.tests], [3, '📦 Containers', counts.containers]] as [StepIndex, string, number][])
            .map(([target, label, count]) => (
              <button key={label} className={`sr-flowbtn${step + 1 === target ? ' sr-next' : ''}`}
                onClick={() => void goToStep(target)}>
                {label} <span className="sr-cnt">{count}</span>
              </button>
            ))}
          <span style={{ flex: 1 }} />
          {stepArrows()}
        </div>}

        {/* The bottom Save bar repeats only on the long Registration step. */}
        {step === 0 && (
          <div className="sr-savebar" style={{ justifyContent: 'flex-end' }}>
            {saveTrio()}
          </div>
        )}
      </div>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <NewLookupDialog
        kind={newLookupKind}
        clientName={header.client}
        onClose={() => { setNewLookupKind(null); setNewLookupField(null); }}
        onAdd={addLookupRow}
        onWarn={m => notify.warning(m)}
      />

      {importKind && (
        <ImportDialog
          kind={importKind}
          onClose={() => setImportKind(null)}
          onImport={() => {
            setImportKind(null);
            // TODO(AI-29131): the import endpoint returns the pre-logged samples
            // / quote lines; for now it seeds the rows from No Of Samples.
            if (currentJobId) ensureSamples(currentJobId);
            notify.success('Samples and tests imported.');
          }}
        />
      )}

      {/* Project Notes — XAF-style two-level popup (list → detail).
          Matches ProjectNotesViewController: 1300×600 list with toolbar,
          750×600 detail with Title / Date / Author / Sort / rich-text Note. */}
      {notesOpen && (
        <ProjectNotesDialog
          jobId={currentJobId ?? ''}
          user={user}
          now={nowLocal}
          notes={tabs.notes}
          setNotes={notes => setTabs({ ...tabs, notes })}
          onClose={() => setNotesOpen(false)}
          onWarn={m => notify.warning(m)}
          confirmDelete={(what, run) =>
            confirmAction('Delete', `Delete ${what}? This cannot be undone.`, run, true)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Which header field each lookup's quick-add feeds back into. */
const FIELD_FOR_LOOKUP: Partial<Record<SrLookupKey, keyof SrHeader>> = {
  client: 'client', contact: 'clientContact', project: 'projectId',
  category: 'projectCategory', pm: 'projectManager', payment: 'paymentStatus',
  inact: 'jobInactivation', permit: 'permit', cocid: 'cocId',
  relinq: 'relinquishedBy', collector: 'collector',
};

/** A provisional, human-readable Job ID for the design pass.
 *  TODO(AI-29131): the REAL id comes from `ISequentialIdGenerator` on the
 *  server (race-safe, §14a Rule B) — never mint an id in the browser. */
/** Peel the server-side error message out of an axios error (or any thrown
 *  Error) so the user sees the real cause instead of a generic banner. Mirrors
 *  the pattern used by QcBatchDetailPage / PrepBatchDetailPage. */
// ─────────────────────────────────────────────────────────────────────────────
// ProjectNotesDialog — XAF-style two-level popup (list → detail)
// Matches ProjectNotesViewController: 1300×600 list with toolbar,
// 750×600 detail with Title / Date / Author / Sort / rich-text Note.
// ─────────────────────────────────────────────────────────────────────────────
function ProjectNotesDialog({
  jobId, user, now, notes, setNotes, onClose, onWarn, confirmDelete,
}: {
  jobId: string;
  user: string;
  now: string;
  notes: SrNote[];
  setNotes: (notes: SrNote[]) => void;
  onClose: () => void;
  onWarn: (msg: string) => void;
  confirmDelete: (label: string, action: () => void) => void;
}) {
  const [editing, setEditing] = useState<SrNote | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const bodyRef = useRef<HTMLDivElement>(null);

  const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

  const notAuthor = (n: SrNote) => n.author !== user;

  const blankNote = (): SrNote => ({
    key: nextKey('note'), oid: '', title: '', author: user, authorOid: '',
    date: now, sort: 0, html: '', plainText: '',
    sourceId: jobId, noteSource: 'Sample Registration',
  });

  const save = () => {
    if (!editing) return;
    if (!editing.title.trim()) { onWarn('Enter the Title.'); return; }
    const html = bodyRef.current?.innerHTML ?? editing.html;
    const plain = stripHtml(html);
    if (!plain) { onWarn('Enter the Note text.'); return; }
    const rec: SrNote = { ...editing, html, plainText: plain };
    const exists = notes.some(n => n.key === rec.key);
    setNotes(exists ? notes.map(n => (n.key === rec.key ? rec : n)) : [...notes, rec]);
    setEditing(null);
  };

  // Per-row edit — opens THIS note's editor from the inline pencil in the Edit
  // column (no single toolbar Edit button; the user edits the exact note without
  // ticking it first).  Author-only: a non-author gets the ALPACA banner and the
  // row shows no pencil, so this is the double-guard (§14a Rule C).
  const editRow = (n: SrNote) => {
    if (notAuthor(n)) { onWarn('You are not authorized to modify a note created by another author.'); return; }
    setEditing({ ...n });
  };

  const tryDelete = () => {
    const picked = notes.filter(n => sel.has(n.key));
    if (picked.some(notAuthor)) {
      onWarn('You are not authorized to modify a note created by another author.');
      return;
    }
    confirmDelete(`${sel.size} note(s)`, () => {
      setNotes(notes.filter(n => !sel.has(n.key)));
      setSel(new Set());
    });
  };

  const cols: AppColumn<SrNote>[] = [
    // Per-row Edit — inline pencil in a dedicated "Edit" column (replaces the
    // single toolbar Edit button; edit is a ROW action). Author-only: a
    // non-author sees a dash, not the pencil (the editRow guard still enforces).
    {
      field: '__edit', header: 'Edit', width: 52, align: 'center',
      sortable: false, filterable: false, noExport: true,
      render: n => (notAuthor(n)
        ? <span title="Only the author can edit this note." style={{ color: 'var(--ai-muted)' }}>—</span>
        : <button className="sr-edit-cell" title="Edit note"
            onClick={e => { e.stopPropagation(); editRow(n); }}>
            <i className="ti ti-edit" />
          </button>),
    },
    { field: 'title', header: 'Title', width: 200 },
    { field: 'plainText', header: 'Note', width: 360, text: r => r.plainText || stripHtml(r.html) },
    { field: 'author', header: 'Author', width: 140 },
    { field: 'date', header: 'Date', width: 170, text: r => fmtLocalDateTime(r.date) },
    { field: 'sort', header: 'Sort', type: 'number', width: 70 },
  ];

  return (
    <>
      <AppDialog open
        title={jobId ? `Project Notes — ${jobId}` : 'Project Notes'}
        icon="ti-note" width={1300} className="sr-dlg"
        onClose={onClose}
        footer={<button className="ca-btn" onClick={onClose}>Close</button>}>
        <AppToolbar
          onNew={() => setEditing(blankNote())}
          onDelete={tryDelete}
          deleteDisabled={sel.size === 0}
        />
        <AppDataGrid<SrNote>
          rows={notes} columns={cols} getRowId={r => r.key}
          selectable selectedIds={sel} onSelectionChange={setSel}
          onRowDoubleClick={editRow}
          defaultPageSize={20} emptyText="No notes yet."
        />
      </AppDialog>

      {editing && (
        <AppDialog open title="Note"
          icon="ti-note" width={750} className="sr-dlg"
          onClose={() => setEditing(null)}
          footer={<>
            <button className="ca-btn" onClick={() => setEditing(null)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={save}>OK</button>
          </>}>
          <div className="sr-note-grid">
            <label className="req">Title:</label>
            <div className="sr-note-val">
              {!editing.title.trim() && <span className="sr-val-icon" title="Required">⊘</span>}
              <input value={editing.title} className={editing.title.trim() ? undefined : 'sr-req-empty'}
                onChange={e => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <label>Business Object:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />

            <label>Source ID:</label>
            <input value={editing.sourceId || jobId} readOnly />
            <label>Author:</label>
            <input value={editing.author} readOnly />

            <label>Name Source:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />
            <label>Date:</label>
            <input value={fmtLocalDateTime(editing.date)} readOnly />
          </div>
          <div className="sr-rte-wrap">
            <span className="sr-rte-tab">Note</span>
            <div className="sr-rte">
              <div className="sr-rte-bar">
                <button className="sr-rte-btn" onClick={() => document.execCommand('undo')} title="Undo"><i className="ti ti-arrow-back-up" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('redo')} title="Redo"><i className="ti ti-arrow-forward-up" /></button>
                <span className="sr-rte-sep" />
                <button className="sr-rte-btn" onClick={() => document.execCommand('copy')} title="Copy"><i className="ti ti-copy" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('cut')} title="Cut"><i className="ti ti-cut" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('paste')} title="Paste"><i className="ti ti-clipboard" /></button>
                <span className="sr-rte-sep" />
                <button className="sr-rte-dd sr-rte-font" onClick={() => {}}>Calibri</button>
                <button className="sr-rte-dd" onClick={() => document.execCommand('bold')}>Font</button>
                <button className="sr-rte-dd" onClick={() => {}}>Paragraph</button>
                <button className="sr-rte-dd" onClick={() => {}}>Styles</button>
                <button className="sr-rte-dd" onClick={() => {}}>Editing</button>
              </div>
              <div ref={bodyRef} className="sr-rte-body" contentEditable suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: editing.html }} />
            </div>
          </div>
        </AppDialog>
      )}
    </>
  );
}

function apiErrMsg(e: unknown, fallback: string): string {
  const er = e as {
    response?: { data?: { error?: { message?: string } } };
    message?: string;
  };
  return er.response?.data?.error?.message ?? er.message ?? fallback;
}

function provisionalJobId(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  return `${stamp}${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function cloneLookups(): Record<SrLookupKey, SrLookup> {
  const out = {} as Record<SrLookupKey, SrLookup>;
  for (const [k, v] of Object.entries(LOOKUPS) as [SrLookupKey, SrLookup][]) {
    out[k] = { cols: [...v.cols], rows: v.rows.map(r => [...r]) };
  }
  return out;
}

function splitList(v: string): string[] {
  return v
    .split(';')
    .map(s => s.trim())
    .filter(Boolean);
}

function mapDetailToHeader(
  d: SampleRegistrationDetailDto,
  registrationType: string,
  now: string,
  user: string,
): SrHeader {
  return {
    ...emptyHeader(registrationType, now, user),
    jobId: d.jobId ?? '',
    // Received Date is a datetime-local input — needs YYYY-MM-DDTHH:mm, so use
    // toDateTimeInput (toDateInput returns date-only, which the input rejects →
    // blank field on edit).
    receivedDate: toDateTimeInput(d.receivedDate) || now,
    receivedBy: d.receivedBy ?? '',
    createdBy: d.createdBy ?? user,
    client: d.client ?? '',
    clientOid: d.clientOid ?? '',
    clientAddress: d.clientAddress ?? '',
    clientPhone: d.clientPhone ?? '',
    clientContact: d.clientContact ?? '',
    quoteId: d.quoteId ?? '',
    poNumber: d.poNumber ?? '',
    paymentStatus: d.paymentStatus ?? '',
    manifestNumber: d.manifestNumber === null || d.manifestNumber === undefined ? '' : String(d.manifestNumber),
    projectManager: d.projectManager ?? '',
    dateExpect: toDateInput(d.dateExpect),
    relinquishedBy: splitList(d.relinquishedBy ?? ''),
    collector: splitList(d.collector ?? ''),
    dateCollected: toDateInput(d.dateCollected),
    timeCollected: d.timeCollected ?? '',
    projectId: d.projectId ?? '',
    projectName: d.projectName ?? '',
    projectLocation: d.projectLocation ?? '',
    permit: d.permit ?? '',
    projectCategory: d.projectCategory ?? '',
    cocId: d.cocId ?? '',
    invoiceContact: d.invoiceContact ?? '',
    registrationType: d.registrationType || registrationType,
    jobInactivation: d.jobInactivation ?? '',
    status: d.statusName ?? 'Pending Submission',
    matrices: splitList(d.matrices ?? ''),
    testTemplate: splitList(d.testTemplate ?? ''),
    testTemplateMethods: {},
    sampleCategory: splitList(d.sampleCategory ?? ''),
    coolerId: d.coolerId ?? '',
    noOfSamples: d.noOfSamples && d.noOfSamples > 0 ? String(d.noOfSamples) : '1',
    remark: d.remark ?? '',
    tat: d.tat ?? '',
    dueDate: toDateInput(d.dueDate),
    reportTemplate: d.reportTemplate ?? '',
    eddTemplate: d.eddTemplate ?? '',
    // API returns the EDDFormat enum int; convert back to the display label so
    // the picker shows "Export to CSV", not "3".
    eddFormat: eddFormatFromInt(d.eddFormat),
    reportDeliveryAddress: d.reportDeliveryAddress ?? '',
    reportDeliveryMethod: d.reportDeliveryMethod ?? '',
  };
}

function mapLookupRows(rows: SampleRegistrationLookupRowDto[]): Record<SrLookupKey, SrLookup> {
  const next = cloneLookups();
  // Every lookup keeps a display→Oid map (populated from Col5 of the SP payload)
  // so persist() can resolve the FK at Save time — the field stores the display
  // for UX, the map holds the Oid the API actually needs.
  for (const k of Object.keys(next) as SrLookupKey[]) {
    next[k].oidByLabel = {};
  }
  for (const row of rows) {
    const key = row.lookupKey as SrLookupKey;
    if (!(key in next)) continue;
    if (key === 'edd') continue;
    const label = row.col1 ?? '';
    const oid   = row.col5 ?? '';
    // Test lookup surfaces Test / Method / IsGroup / Component. Its selection VALUE is the
    // XAF composite "TestName|MethodNumber" (col1|col2) so two same-named tests
    // with different methods are distinct and each maps to its own TestMethod
    // Oid. oidByLabel is therefore keyed by the composite, not the bare name.
    if (key === 'test') {
      const method   = row.col2 ?? '';
      const composite = method ? `${label}|${method}` : label;
      if (composite && oid) next.test.oidByLabel![composite] = oid;
      next.test.rows.push([label, method, row.col3 ?? '', row.col4 ?? '']);
      continue;
    }
    if (label && oid) {
      next[key].oidByLabel![label] = oid;
    }
    const width = next[key].cols.length;
    const values = [row.col1, row.col2, row.col3, row.col4];
    const display = values.slice(0, width);
    // Client names can collide (e.g. two "City of Houston"). Carry the exact
    // Oid as a HIDDEN trailing element (the picker clips rendering to cols.length)
    // so applyPick captures THIS row's client — not an ambiguous name lookup —
    // which the client-scoped Project / Collector / Contact / Quote lists need.
    if (key === 'client' && oid) display.push(oid);
    next[key].rows.push(display);
  }
  return next;
}

function seedHeader(
  jobId: string | null, registrationType: string, now: string, user: string,
  seed?: SampleRegistrationDetailPageProps['seed'],
): SrHeader {
  const h = emptyHeader(registrationType, now, user);
  if (!jobId) return h;
  return {
    ...h,
    jobId,
    status: seed?.status ?? 'Pending Submission',
    client: seed?.client ?? '',
    projectName: seed?.projectName ?? '',
    receivedDate: seed?.receivedDate ?? now,
    dueDate: seed?.dueDate ? toDateInput(seed.dueDate) : '',
  };
}

/** Returns an empty tab state.  The first received-date row is seeded via a
 *  `useEffect` once the user picks a matrix (XAF parity — see rdAutoSeededRef). */
function seedTabs(_now: string, _user: string): SrTabState {
  return emptyTabState();
}

function SummaryBlock({ header, jobId, counts, charge }: {
  header: SrHeader; jobId: string | null;
  counts: { samples: number; tests: number; containers: number };
  charge: ReturnType<typeof chargeRows>;
}) {
  const row = (label: string, value: string) => (
    <div className="sr-sum-row" key={label}><span>{label}</span><span>{value || '—'}</span></div>
  );
  return (
    <div className="sr-sum">
      <div>
        <div className="sr-sum-h">Registration Summary</div>
        {row('Job ID', jobId ?? 'Autogenerate')}
        {row('Client', header.client)}
        {row('Project ID', header.projectId)}
        {row('Sample Matrix', header.matrices.join('; '))}
        {row('Received Date', fmtLocalDateTime(header.receivedDate))}
        {row('Due Date', fmtLocalDate(header.dueDate))}
        {row('Samples', String(counts.samples))}
        {row('Test assignments', String(counts.tests))}
        {row('Status', header.status)}
      </div>
      <div>
        <div className="sr-sum-h">Analysis Charge</div>
        <table className="sr-charge">
          <thead>
            <tr>
              <th>Test</th><th>Method</th><th className="sr-num">Qty</th>
              <th className="sr-num">Unit Price</th><th className="sr-num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {charge.rows.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', fontStyle: 'italic', color: '#9aa1ad', padding: 16 }}>
                No tests.
              </td></tr>
            ) : charge.rows.map(r => (
              <tr key={r.test}>
                <td>{r.test}</td><td>{r.method}</td>
                <td className="sr-num">{r.qty}</td>
                <td className="sr-num">${r.unitPrice.toFixed(2)}</td>
                <td className="sr-num">${r.amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={4} className="sr-num">Total</td><td className="sr-num">${charge.total.toFixed(2)}</td></tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/** Import Samples (from a Quote) / Import Pre-Log (COC rows for a Project). */
function ImportDialog({ kind, onClose, onImport }: {
  kind: 'quote' | 'coc'; onClose: () => void; onImport: () => void;
}) {
  const quote = kind === 'quote';
  const headers = quote
    ? ['Price Code', 'Sample Matrix', 'Test', 'Method', 'Unit Price']
    : ['COC_ID', 'COCName', 'ProjectID', 'ProjectName', 'ProjectLocation', 'SampleMatrix'];
  // TODO(AI-29131): these rows come from the import endpoint.
  const rows = quote
    ? [
      ['DW-TC', 'Drinking Water', 'Total Coliform', 'SM 9223B', '$28.00'],
      ['DW-EC', 'Drinking Water', 'E. coli', 'SM 9223B', '$28.00'],
      ['DW-NO3', 'Drinking Water', 'Nitrate', 'EPA 300.0', '$22.00'],
    ]
    : [
      ['COC0170', 'LCR Belleauwood', 'LCR Belleau Woods', 'LCR Belleau Woods', 'Belleau Woods', 'Water'],
      ['COC0171', 'Main Biweekly POE', 'Main Biweekly POE', 'Main Biweekly POE', 'Main System', 'Water'],
    ];
  return (
    <AppDialog open width={760} icon="ti-download"
      title={quote ? 'Import Samples' : 'Import Pre-Log'} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={onImport}>OK</button>
      </>}>
      <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
        <table className="sr-pick-table">
          <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(r => <tr key={r[0]}>{r.map((c, i) => <td key={i}>{c}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
    </AppDialog>
  );
}
